
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

export default function MedicalMissionPage() {
  const services = [
    {
      title: 'Medical Outreach',
      description: 'Free healthcare services in underserved communities',
      icon: 'ri-stethoscope-line',
      stats: '50,000+ patients served'
    },
    {
      title: 'Food Distribution',
      description: 'Nutritional support for vulnerable families',
      icon: 'ri-restaurant-line',
      stats: '100,000+ meals provided'
    },
    {
      title: 'Economic Empowerment',
      description: 'Skills training and microfinance programs',
      icon: 'ri-hand-coin-line',
      stats: '5,000+ lives empowered'
    },
    {
      title: 'Community Care',
      description: 'Holistic support for families in need',
      icon: 'ri-heart-line',
      stats: '200+ communities reached'
    }
  ];

  const programs = [
    {
      title: 'Mobile Medical Clinics',
      description: 'Bringing healthcare directly to remote communities through mobile medical units equipped with essential medical supplies and staffed by qualified healthcare professionals.',
      image: 'https://readdy.ai/api/search-image?query=Mobile%20medical%20clinic%20in%20African%20rural%20setting%2C%20healthcare%20workers%20serving%20patients%2C%20simple%20background%2C%20warm%20lighting%2C%20compassionate%20care%2C%20medical%20outreach%20context&width=400&height=300&seq=mobile1&orientation=landscape'
    },
    {
      title: 'Nutrition Programs',
      description: 'Addressing malnutrition through feeding programs, nutrition education, and sustainable food security initiatives in vulnerable communities.',
      image: 'https://readdy.ai/api/search-image?query=Community%20feeding%20program%20in%20Africa%2C%20children%20receiving%20nutritious%20meals%2C%20simple%20background%2C%20warm%20lighting%2C%20hope%20and%20care%2C%20nutrition%20outreach%20context&width=400&height=300&seq=nutrition1&orientation=landscape'
    },
    {
      title: 'Skills Training Centers',
      description: 'Empowering individuals with vocational skills, entrepreneurship training, and microfinance opportunities to break the cycle of poverty.',
      image: 'https://readdy.ai/api/search-image?query=African%20vocational%20training%20center%2C%20people%20learning%20practical%20skills%2C%20simple%20background%2C%20warm%20lighting%2C%20empowerment%20and%20education%2C%20community%20development%20context&width=400&height=300&seq=skills1&orientation=landscape'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section 
        className="relative py-32 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(184, 20, 74, 0.8), rgba(184, 20, 74, 0.8)), url('https://readdy.ai/api/search-image?query=Healthcare%20workers%20serving%20in%20African%20community%2C%20medical%20outreach%20scene%2C%20compassionate%20care%2C%20simple%20background%2C%20warm%20golden%20lighting%2C%20hope%20and%20healing%20atmosphere%2C%20ministry%20of%20mercy%20context&width=1920&height=800&seq=medicalhero&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-5xl font-bold mb-6">Meal and Medical Mission</h1>
          <p className="text-xl max-w-4xl mx-auto mb-8">
            Ministry of healing, compassion, and economic empowerment for the poor and vulnerable
          </p>
          <Link 
            to="/join"
            className="bg-white text-[#b8144a] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
          >
            Join Our Mission
          </Link>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Impact</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Through the Meal and Medical Mission, we demonstrate Christ's love through practical care, 
              bringing healing, hope, and empowerment to communities in need.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <div key={index} className="text-center bg-gray-50 p-6 rounded-lg shadow-lg">
                <div className="w-16 h-16 bg-[#b8144a] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${service.icon} text-white text-xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-700 mb-4">{service.description}</p>
                <p className="text-[#b8144a] font-semibold text-sm">{service.stats}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Programs</h2>
            <p className="text-xl text-gray-600">Comprehensive care addressing physical, nutritional, and economic needs</p>
          </div>

          <div className="space-y-16">
            {programs.map((program, index) => (
              <div key={index} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                  <h3 className="text-3xl font-bold text-gray-900 mb-6">{program.title}</h3>
                  <p className="text-lg text-gray-700 leading-relaxed">{program.description}</p>
                  <div className="mt-8">
                    <Link 
                      to="/join"
                      className="bg-[#b8144a] hover:bg-[#a01240] text-white px-6 py-3 rounded-full font-semibold transition-colors whitespace-nowrap cursor-pointer"
                    >
                      Get Involved
                    </Link>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:col-start-1' : ''}>
                  <img 
                    src={program.image}
                    alt={program.title}
                    className="rounded-lg shadow-lg object-cover w-full h-80"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gray-50 rounded-lg p-12 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
            <p className="text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
              "The Meal and Medical Mission exists to demonstrate the love of Christ through practical compassion, 
              bringing healing to the sick, food to the hungry, and hope to the hopeless. We believe that the Gospel 
              is not just words but power - power to heal, restore, and transform lives and communities."
            </p>
            <div className="mt-8">
              <p className="text-lg text-[#b8144a] font-semibold">
                - Pastor Mrs. Clementina Akinnubi, Director of Welfare and Family Affairs
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Partnership Opportunities */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Partnership Opportunities</h2>
            <p className="text-xl text-gray-600">Join us in making a difference</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-[#b8144a] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-user-heart-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Volunteer</h3>
              <p className="text-gray-700 mb-6">
                Join our medical missions, feeding programs, and community outreach initiatives as a volunteer.
              </p>
              <Link 
                to="/join"
                className="text-[#b8144a] hover:text-[#a01240] font-semibold cursor-pointer"
              >
                Learn More →
              </Link>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-[#b8144a] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hand-heart-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Sponsor</h3>
              <p className="text-gray-700 mb-6">
                Sponsor medical equipment, food supplies, or training programs to multiply our impact.
              </p>
              <Link 
                to="/join"
                className="text-[#b8144a] hover:text-[#a01240] font-semibold cursor-pointer"
              >
                Partner With Us →
              </Link>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-[#b8144a] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hospital-line text-white text-xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Healthcare Professional</h3>
              <p className="text-gray-700 mb-6">
                Use your medical skills to serve in our mobile clinics and community health programs.
              </p>
              <Link 
                to="/join"
                className="text-[#b8144a] hover:text-[#a01240] font-semibold cursor-pointer"
              >
                Join Our Team →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-[#b8144a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-6">Be the Hands and Feet of Christ</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            Join the Meal and Medical Mission and become part of God's strategy to bring healing, 
            hope, and transformation to communities in need around the world.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/join"
              className="bg-white text-[#b8144a] px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              Join the Mission
            </Link>
            <Link 
              to="/training"
              className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-[#b8144a] transition-colors whitespace-nowrap cursor-pointer"
            >
              Medical Training
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
